import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "@/store/useAuthStore";
import { queryKeys } from "@/constants/queryKeys";
import { chatMessageAction, markChatRead } from "@/services/api/chat";
import { useMessagesStore } from "../store/useMessagesStore";
import type { ChatMessage, Conversation, TimelineItem } from "../data/types";

/** Inner bag can be ChatMessage, nested {conversation_id, message}, or event fields. */
type MessageBag = {
  id?: string;
  conversation_id?: string;
  conversation?: string | Conversation;
  message?: ChatMessage;
  message_ids?: string[];
  status?: "sent" | "delivered" | "read";
  scope?: string;
  user_id?: number;
  user_name?: string;
  is_typing?: boolean;
  last_read_at?: string;
  sender?: ChatMessage["sender"];
  body?: string;
  msg_type?: ChatMessage["msg_type"];
  created_at?: string;
  client_id?: string | null;
  attachment_name?: string;
  is_deleted?: boolean;
  [key: string]: unknown;
};

type SocketPayload = {
  from?: string;
  type?: string;
  /** Plan contract: conversation_id at top level */
  conversation_id?: string;
  message?: MessageBag;
  user?: number;
};

type TimelineCache = { items: TimelineItem[]; dial_number: string };

const STATUS_RANK: Record<string, number> = {
  sent: 0,
  delivered: 1,
  read: 2,
};

function resolveConversationId(payload: SocketPayload): string | undefined {
  const bag = payload.message;
  const fromBag =
    bag?.conversation_id ||
    (typeof bag?.conversation === "string" ? bag.conversation : undefined) ||
    (typeof bag?.message?.conversation === "string"
      ? bag.message.conversation
      : undefined);
  const top = payload.conversation_id;
  return top ? String(top) : fromBag ? String(fromBag) : undefined;
}

/**
 * Plan: envelope.message = ChatMessage.
 * Legacy (buggy): envelope.message = { conversation_id, message: ChatMessage }.
 */
function unwrapChatMessage(payload: SocketPayload): ChatMessage | null {
  const bag = payload.message;
  if (!bag || typeof bag !== "object") return null;

  const nested = bag.message;
  if (nested && typeof nested === "object" && nested.id) {
    return nested;
  }

  if (bag.id && (bag.msg_type != null || bag.body != null || bag.sender != null)) {
    return bag as unknown as ChatMessage;
  }

  return null;
}

function patchMessageStatuses(
  queryClient: ReturnType<typeof useQueryClient>,
  conversationId: string,
  messageIds: string[],
  status: "sent" | "delivered" | "read",
) {
  const timelineKey = queryKeys.chatTimeline(conversationId);
  queryClient.setQueryData<TimelineCache>(timelineKey, (prev) => {
    if (!prev) return prev;
    const idSet = new Set(messageIds.map(String));
    return {
      ...prev,
      items: prev.items.map((item) => {
        if (item.kind !== "message") return item;
        if (!idSet.has(String(item.message.id))) return item;
        const current = item.message.status || "sent";
        if ((STATUS_RANK[status] ?? 0) <= (STATUS_RANK[current] ?? 0)) return item;
        return {
          ...item,
          message: { ...item.message, status },
        };
      }),
    };
  });
}

function bumpConversationPreview(
  queryClient: ReturnType<typeof useQueryClient>,
  conversationId: string,
  message: ChatMessage,
  unreadDelta: number,
) {
  queryClient.setQueriesData<Conversation[]>(
    { queryKey: ["chat-conversations"] },
    (list) => {
      if (!Array.isArray(list)) return list;
      return list.map((conv) => {
        if (conv.id !== conversationId) return conv;
        return {
          ...conv,
          preview: message.body || message.attachment_name || conv.preview,
          time: message.created_at,
          unread: Math.max(0, (conv.unread || 0) + unreadDelta),
          last_message_at: message.created_at,
        };
      });
    },
  );
}

function upsertTimelineMessage(
  queryClient: ReturnType<typeof useQueryClient>,
  conversationId: string,
  message: ChatMessage,
): { inserted: boolean; hadCache: boolean } {
  const timelineKey = queryKeys.chatTimeline(conversationId);
  const prev = queryClient.getQueryData<TimelineCache>(timelineKey);
  const id = `message:${message.id}`;

  if (!prev) {
    queryClient.setQueryData<TimelineCache>(timelineKey, {
      items: [
        {
          id,
          kind: "message",
          occurred_at: message.created_at,
          message,
        },
      ],
      dial_number: "",
    });
    return { inserted: true, hadCache: false };
  }

  const existingIdx = prev.items.findIndex((item) => item.id === id);
  if (existingIdx >= 0) {
    queryClient.setQueryData<TimelineCache>(timelineKey, {
      ...prev,
      items: prev.items.map((item, idx) => {
        if (idx !== existingIdx || item.kind !== "message") return item;
        const current = item.message.status || "sent";
        const incoming = message.status || "sent";
        const keepStatus =
          (STATUS_RANK[incoming] ?? 0) > (STATUS_RANK[current] ?? 0)
            ? incoming
            : current;
        return {
          ...item,
          message: {
            ...item.message,
            ...message,
            status: keepStatus,
            from_me: message.from_me,
          },
        };
      }),
    });
    return { inserted: false, hadCache: true };
  }

  queryClient.setQueryData<TimelineCache>(timelineKey, {
    ...prev,
    items: [
      ...prev.items,
      {
        id,
        kind: "message",
        occurred_at: message.created_at,
        message,
      },
    ],
  });
  return { inserted: true, hadCache: true };
}

export function useChatSocket(enabled = true) {
  const queryClient = useQueryClient();
  const accessToken = useAuthStore((s) => s.accessToken);
  const profile = useAuthStore((s) => s.profile);
  const myUserId = useAuthStore((s) => s.profile?.id ?? s.user?.id);
  const socketUrl = profile?.chat_socket_url || "";
  const wsRef = useRef<WebSocket | null>(null);
  const retryRef = useRef(0);
  const timerRef = useRef<number | null>(null);
  const typingTimers = useRef<Record<string, number>>({});

  useEffect(() => {
    if (!enabled || !accessToken || !socketUrl) return;

    let closed = false;

    const connect = () => {
      if (closed) return;
      try {
        const ws = new WebSocket(socketUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          retryRef.current = 0;
        };

        ws.onmessage = (event) => {
          try {
            const payload = JSON.parse(String(event.data)) as SocketPayload;
            if (payload.from !== "papaya_chat") return;

            if (payload.type === "typing") {
              const conversationId = resolveConversationId(payload);
              const userId = payload.message?.user_id;
              if (!conversationId || !userId || userId === myUserId) return;

              const setPeerTyping = useMessagesStore.getState().setPeerTyping;
              if (payload.message?.is_typing === false) {
                setPeerTyping(conversationId, null);
                if (typingTimers.current[conversationId]) {
                  window.clearTimeout(typingTimers.current[conversationId]);
                  delete typingTimers.current[conversationId];
                }
                return;
              }

              const expiresAt = Date.now() + 3500;
              setPeerTyping(conversationId, {
                userId,
                name: payload.message?.user_name || "Someone",
                expiresAt,
              });
              if (typingTimers.current[conversationId]) {
                window.clearTimeout(typingTimers.current[conversationId]);
              }
              typingTimers.current[conversationId] = window.setTimeout(() => {
                const current =
                  useMessagesStore.getState().typingByConversation[conversationId];
                if (current && current.expiresAt <= Date.now()) {
                  setPeerTyping(conversationId, null);
                }
              }, 3600);
              return;
            }

            if (payload.type === "message_status") {
              const conversationId = resolveConversationId(payload);
              const messageIds = payload.message?.message_ids || [];
              const status = payload.message?.status;
              if (conversationId && messageIds.length && status) {
                patchMessageStatuses(queryClient, conversationId, messageIds, status);
              }
              return;
            }

            if (payload.type === "message_deleted") {
              const conversationId = resolveConversationId(payload);
              const deleted = unwrapChatMessage(payload);
              if (!conversationId || !deleted) return;
              const timelineKey = queryKeys.chatTimeline(conversationId);
              queryClient.setQueryData<TimelineCache>(timelineKey, (prev) => {
                if (!prev) return prev;
                const id = `message:${deleted.id}`;
                return {
                  ...prev,
                  items: prev.items.map((item) => {
                    if (item.kind !== "message" || item.id !== id) return item;
                    return {
                      ...item,
                      message: {
                        ...item.message,
                        ...deleted,
                        is_deleted: true,
                        body: "",
                        from_me:
                          myUserId != null &&
                          Number(deleted.sender?.id) === Number(myUserId),
                      },
                    };
                  }),
                };
              });
              void queryClient.invalidateQueries({ queryKey: ["chat-conversations"] });
              return;
            }

            if (payload.type === "message") {
              const conversationId = resolveConversationId(payload);
              const rawMessage = unwrapChatMessage(payload);

              // Parse failed → badge refresh only (same symptom user saw before fix)
              if (!conversationId || !rawMessage?.id) {
                void queryClient.invalidateQueries({ queryKey: ["chat-conversations"] });
                return;
              }

              const isMine =
                myUserId != null &&
                Boolean(rawMessage.sender?.id) &&
                Number(rawMessage.sender?.id) === Number(myUserId);

              const message: ChatMessage = {
                ...rawMessage,
                conversation:
                  typeof rawMessage.conversation === "string"
                    ? rawMessage.conversation
                    : conversationId,
                from_me: isMine,
                status: rawMessage.status || (isMine ? "sent" : rawMessage.status),
              };

              const { inserted, hadCache } = upsertTimelineMessage(
                queryClient,
                conversationId,
                message,
              );

              // If we seeded an empty cache, soft-refetch full timeline in background
              if (!hadCache && !isMine) {
                void queryClient.invalidateQueries({
                  queryKey: queryKeys.chatTimeline(conversationId),
                });
              }

              useMessagesStore.getState().setPeerTyping(conversationId, null);

              if (isMine) {
                bumpConversationPreview(queryClient, conversationId, message, 0);
                return;
              }

              void chatMessageAction(conversationId, {
                message_id: message.id,
                action: "delivered",
              }).catch(() => undefined);

              const viewingId = useMessagesStore.getState().viewingConversationId;
              const isViewing = viewingId === conversationId;

              if (isViewing) {
                void markChatRead(conversationId).then(() => {
                  bumpConversationPreview(queryClient, conversationId, message, 0);
                  queryClient.setQueriesData<Conversation[]>(
                    { queryKey: ["chat-conversations"] },
                    (list) => {
                      if (!Array.isArray(list)) return list;
                      return list.map((conv) =>
                        conv.id === conversationId ? { ...conv, unread: 0 } : conv,
                      );
                    },
                  );
                });
              } else if (inserted) {
                bumpConversationPreview(queryClient, conversationId, message, 1);
                void queryClient.invalidateQueries({ queryKey: ["chat-conversations"] });
              }

              return;
            }

            if (payload.type === "conversation_upsert") {
              void queryClient.invalidateQueries({ queryKey: ["chat-conversations"] });
              return;
            }

            if (payload.type === "read") {
              void queryClient.invalidateQueries({ queryKey: ["chat-conversations"] });
              return;
            }
          } catch {
            // ignore malformed frames
          }
        };

        ws.onclose = () => {
          wsRef.current = null;
          if (closed) return;
          const delay = Math.min(10_000, 1000 * 2 ** retryRef.current);
          retryRef.current += 1;
          timerRef.current = window.setTimeout(connect, delay);
        };

        ws.onerror = () => {
          ws.close();
        };
      } catch {
        const delay = Math.min(10_000, 1000 * 2 ** retryRef.current);
        retryRef.current += 1;
        timerRef.current = window.setTimeout(connect, delay);
      }
    };

    connect();

    return () => {
      closed = true;
      if (timerRef.current) window.clearTimeout(timerRef.current);
      for (const id of Object.values(typingTimers.current)) {
        window.clearTimeout(id);
      }
      typingTimers.current = {};
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [accessToken, enabled, myUserId, queryClient, socketUrl]);
}
